/* Angular parity — `critical-notifications.component.ts:64-68` +
 * `utils/card-expiry.ts:11-27 updateAndRearrangeNotifications`.
 *
 * The BE ships critical notifications with generic strings. Three
 * known types are rewritten client-side to the copy the design
 * system ships, and the same three float to the top of the list in
 * that order. Notifications the BE returns but the client doesn't
 * override are kept as-is in their original BE order after the
 * overridden trio.
 *
 * Copy pulled from `assets/locales/translation/en.yaml`:
 *   creditCardExpiry / achToCreditcard / proprietaryToCreditcard. */

export type CriticalNotificationOverride = {
  /** BE `notification.type` value to match. */
  type: string;
  /** HTML string rendered via `dangerouslySetInnerHTML`. Kept as an
   *  HTML string (not JSX) so the same string flows through
   *  DOMPurify like BE messages do. */
  message: string;
};

const UPDATE_TODAY_LINK =
  '<a href="/my-account/plan-details" class="alertLinkText">Update today</a>';

/* Order matters — matched entries are floated to the top of the
 * banner list in this exact sequence. */
export const CRITICAL_NOTIFICATION_OVERRIDES: CriticalNotificationOverride[] = [
  {
    type: 'ach-to-creditcard',
    message: `Your payment method on file needs to be updated. ${UPDATE_TODAY_LINK}`,
  },
  {
    type: 'proprietary-to-creditcard',
    message: `Your payment method on file needs to be updated. ${UPDATE_TODAY_LINK}`,
  },
  {
    type: 'credit-card-expiring',
    message: `Your credit card on file expires soon. ${UPDATE_TODAY_LINK}`,
  },
];

/* Angular parity — `critical-notifications.component.html:26-33` +
 * `translation/en.yaml appChrome.discoverCCBanner / updateCC`.
 *
 * A client-synthesised critical notification for Discover-employee
 * users who still have the legacy proprietary card
 * (`billingAccountInfo.accountFound === false`). Prepended to the
 * banner list at render time in `<CriticalNotification>` so it
 * follows the same styling as the BE-supplied ones. The anchor
 * `href` is a placeholder — the click is intercepted and dispatches
 * `requestCardExpiryModalOnPayment()`, opening the Card Expiry
 * Reminder modal directly on the Zuora payment step. */
export const DISCOVER_PROPRIETARY_NOTIFICATION_ID = 'client:discover-update-cc';

export const DISCOVER_PROPRIETARY_NOTIFICATION_TYPE = 'discover-update-cc';

export const DISCOVER_PROPRIETARY_NOTIFICATION_MESSAGE =
  'Due to technical changes outside of our control, updated payment information is required to continue your service. ' +
  '<a href="#" class="alertLinkText">Update now to avoid interruption.</a>';
